import { AppStateEnum } from '../../../main/types/app-state.types.ts';
import { AudioRecordingPartialResult } from '../../../main/types/audio.types.ts';
import { getAudioRecordingUtil } from '../../../main/utils/audio/audio-recording.util.ts';
import { getLocaleUtil } from '../../../main/utils/locale.util.ts';
import { getSentryUtil } from '../../../main/utils/monitoring/sentry.util.ts';
import { getReduxStoreUtil } from '../../../main/utils/redux-store.util.ts';

import { getPronunciationGraderParametersQueryRequest } from '../../requests/get-pronunciation-grader-parameters.query.ts';
import { updateAppStateThunk } from '../../slices/app-state/thunks/update-app-state.thunk.ts';
import {
  BasePronunciationGraderInterface,
  PronunciationGradingResult,
  PronunciationGraderEntry,
  PronunciationGraderState,
  PronunciationWithResult,
  BasePronunciationGraderResultDetails,
} from '../../types/pronunciation-grader.types.ts';
import {
  GraderType,
  LetterPronunciation,
  PhonemeParametersSchema,
} from '../../types/pronunciation-grading-parameters.types.ts';

import { SpeechSuperGradingUtil } from '../speechsuper/speechsuper-grading.util.ts';

/**
 * Handles pronunciation grading for phonemes.
 * Coordinates audio recording and real-time grading using SpeechSuper.
 */
export class PhonemePronunciationGrader implements BasePronunciationGraderInterface {
  private static _instance: PhonemePronunciationGrader;

  static get instance(): PhonemePronunciationGrader {
    if (!PhonemePronunciationGrader._instance) {
      PhonemePronunciationGrader._instance = new PhonemePronunciationGrader();
    }
    return PhonemePronunciationGrader._instance;
  }

  private constructor(
    private audioRecorder = getAudioRecordingUtil(),
    private pronunciations: PronunciationGraderEntry<LetterPronunciation>[] = [],
    private refText: string | null = null
  ) {}

  private state: PronunciationGraderState = {
    isInitialized: false,
    isRecording: false,
    isGrading: false,
  };

  /**
   * Initializes the grader with the reference text to check against.
   * Must be called before starting grading.
   * @param refText - The reference text to grade against
   */
  async initialize(refText: string): Promise<void> {
    if (this.state.isRecording || this.state.isGrading) {
      throw new Error(getLocaleUtil().select('learn:grader_busy'));
    }

    // Get grading parameters
    const result = await getPronunciationGraderParametersQueryRequest({
      graderType: GraderType.PHONEME,
      refText,
    });

    if (!result.success || !result.data) {
      throw new Error(getLocaleUtil().select('learn:grader_initialization_failed'));
    }

    const parameters = PhonemeParametersSchema.parse(result.data.parameters);

    // Initialize grader for correct pronunciation
    const correctGrader = new SpeechSuperGradingUtil();
    correctGrader.initialize({
      graderType: GraderType.PHONEME,
      refText: parameters.pronunciation,
    });
    this.pronunciations.push({
      grader: correctGrader,
      parameters: parameters,
      isCorrect: true,
    });

    // Initialize graders for mispronunciations
    for (const mispronunciation of parameters.commonMispronunciations) {
      const grader = new SpeechSuperGradingUtil();
      grader.initialize({
        graderType: GraderType.PHONEME,
        refText: mispronunciation.pronunciation,
      });
      this.pronunciations.push({
        grader,
        parameters: mispronunciation,
        isCorrect: false,
      });
    }

    this.refText = refText;
    this.state.isInitialized = true;
  }

  /**
   * Starts the grading process.
   * Must be called after initialize.
   * @returns A promise that resolves to true if the pronunciation was correct
   */
  async startGrading(): Promise<PronunciationGradingResult<BasePronunciationGraderResultDetails>> {
    if (!this.state.isInitialized || !this.refText) {
      throw new Error(getLocaleUtil().select('learn:grader_not_initialized'));
    }

    if (this.state.isRecording || this.state.isGrading) {
      throw new Error(getLocaleUtil().select('learn:grader_busy'));
    }

    // Start all graders
    await Promise.all(this.pronunciations.map((pronunciation) => pronunciation.grader.startGrading()));
    this.state.isGrading = true;

    return new Promise((resolve, reject) => {
      // Start recording with real-time processing
      this.audioRecorder
        .record({
          callbacks: {
            onPartialResult: async (audioData) => {
              await this.broadcastAudioDataToGraders(audioData);
            },
          },
          silenceDetection: {
            enabled: true,
            onSilenceDetected: async () => {
              const result = await this.finalizeGrading();
              resolve(result);
            },
          },
        })
        .catch(reject);
    });
  }

  /**
   * Aborts the grading process.
   * @returns A promise that resolves when the grading is aborted
   */
  async abort(): Promise<void> {
    const isRecording = this.state.isRecording;
    const isGrading = this.state.isGrading;
    const pronunciations = this.pronunciations;
    this.cleanup();

    if (isRecording) {
      await this.audioRecorder.stopRecording();
    }
    if (isGrading) {
      await Promise.all(pronunciations.map((pronunciation) => pronunciation.grader.abort()));
    }
  }

  private async broadcastAudioDataToGraders(audioData: AudioRecordingPartialResult): Promise<void[]> {
    return Promise.all(this.pronunciations.map((pronunciation) => pronunciation.grader.sendAudioData(audioData)));
  }

  private async finalizeGrading(): Promise<PronunciationGradingResult<BasePronunciationGraderResultDetails>> {
    try {
      const dispatch = await getReduxStoreUtil().getDispatch();
      await dispatch(updateAppStateThunk({ state: AppStateEnum.Evaluating }));

      // Get results from all graders
      const gradingResults: (PronunciationWithResult<LetterPronunciation> | null)[] = await Promise.all(
        this.pronunciations.map(async (pronunciation) => {
          const result = await pronunciation.grader.stopGrading();
          if (!result) {
            return null;
          }
          return {
            pronunciation,
            result: {
              isCorrect: result.result.overall >= pronunciation.parameters.threshold,
              overall: result.result.overall + (pronunciation.parameters.boost ?? 0),
              pronunciation: result.result.pronunciation,
              fluency: result.result.overall,
            },
          };
        })
      );

      // Find the correct pronunciation result
      const correctPronunciationResult = gradingResults.find((r) => r?.pronunciation.isCorrect);
      const mispronunciationResults = gradingResults.filter((r) => r && !r.pronunciation.isCorrect);
      if (!correctPronunciationResult) {
        throw new Error(getLocaleUtil().select('learn:no_correct_pronunciation'));
      }

      // Check if any mispronunciation has a higher score than the correct one
      const betterMispronunciation = mispronunciationResults.find(
        (r) => r && r.result.isCorrect && r.result.overall > correctPronunciationResult.result.overall
      );

      if (!correctPronunciationResult.result.isCorrect || betterMispronunciation) {
        return {
          isCorrect: false,
          details: { recognizedText: betterMispronunciation?.pronunciation.parameters.pronunciation ?? '' },
        };
      }

      return {
        isCorrect: true,
        details: { recognizedText: correctPronunciationResult.pronunciation.parameters.pronunciation },
      };
    } catch (error) {
      getSentryUtil().trackError(error as Error, {
        grader: 'phoneme_pronunciation',
        refText: this.refText ?? undefined,
      });
      return { isCorrect: false, details: { recognizedText: '' } };
    } finally {
      this.cleanup();
    }
  }

  private cleanup(): void {
    this.state.isRecording = false;
    this.state.isGrading = false;
    this.state.isInitialized = false;
    this.pronunciations = [];
    this.refText = null;
  }
}

export const getPhonemePronunciationGrader = (): PhonemePronunciationGrader => PhonemePronunciationGrader.instance;
